/**
 * Phase 20 Plan 20-02 — RecipientsListTab
 *
 * Paginated table of notification recipients.
 * Features: includeInactive toggle, Add button, expandable phone row.
 */

import type { JSX } from 'react';
import { useState } from 'react';

import { CreateRecipientDialog } from '@/components/notifications/CreateRecipientDialog';
import { EditRecipientDialog } from '@/components/notifications/EditRecipientDialog';
import { RecipientPhonesPanel } from '@/components/notifications/RecipientPhonesPanel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useRecipientsQuery,
  useRecipientDetailQuery,
} from '@/hooks/useNotificationsQueries';
import type { RecipientListItemDto } from '@/types/notifications';

function RecipientExpandedRow({ recipientId }: { readonly recipientId: number }): JSX.Element {
  const { data } = useRecipientDetailQuery(recipientId);

  return (
    <TableRow data-testid={`recipient-expanded-${recipientId}`}>
      <TableCell colSpan={6} className="bg-muted/30 p-4">
        <div className="space-y-3">
          <RecipientPhonesPanel recipientId={recipientId} />
          {data !== undefined && (
            <p className="text-xs text-muted-foreground">
              {data.subscriptions.filter((s) => s.isEnabled).length} subscriptions enabled
            </p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function RecipientsListTab(): JSX.Element {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRecipient, setEditRecipient] = useState<RecipientListItemDto | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading } = useRecipientsQuery({
    includeInactive,
    page,
    pageSize: 20,
  });

  // For EditRecipientDialog we need a RecipientDetailDto — load it lazily
  const { data: editDetail } = useRecipientDetailQuery(
    editRecipient?.id ?? 0,
    editRecipient !== null,
  );

  const recipients = data?.items ?? [];

  const toggleExpand = (id: number): void => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-inactive"
            data-testid="include-inactive-toggle"
            checked={includeInactive}
            onCheckedChange={(v) => { setIncludeInactive(v === true); }}
          />
          <Label htmlFor="include-inactive" className="text-sm">
            Include inactive
          </Label>
        </div>
        <Button
          size="sm"
          onClick={() => { setCreateOpen(true); }}
          data-testid="add-recipient-btn"
        >
          + Add Recipient
        </Button>
      </div>

      {/* Table */}
      <Table data-testid="recipients-list-table">
        <TableHeader>
          <TableRow>
            <TableHead>Display Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Phones</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                Loading…
              </TableCell>
            </TableRow>
          )}
          {!isLoading && recipients.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground" data-testid="recipients-empty">
                No recipients found.
              </TableCell>
            </TableRow>
          )}
          {recipients.map((r) => (
            <>
              <TableRow
                key={r.id}
                data-testid={`recipient-row-${r.id}`}
                className="cursor-pointer"
                onClick={() => { toggleExpand(r.id); }}
              >
                <TableCell>{r.displayName}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.email ?? '—'}</TableCell>
                <TableCell>
                  <span
                    className={
                      r.isActive
                        ? 'rounded bg-green-100 px-1 py-0.5 text-xs text-green-700'
                        : 'rounded bg-gray-100 px-1 py-0.5 text-xs text-gray-500'
                    }
                  >
                    {r.isActive ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>{r.phoneCount}</TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditRecipient(r);
                    }}
                    data-testid={`edit-recipient-btn-${r.id}`}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
              {expandedId === r.id && (
                <RecipientExpandedRow key={`expanded-${r.id}`} recipientId={r.id} />
              )}
            </>
          ))}
        </TableBody>
      </Table>

      {/* Create dialog */}
      <CreateRecipientDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); }}
      />

      {/* Edit dialog — requires full detail */}
      {editRecipient !== null && editDetail !== undefined && (
        <EditRecipientDialog
          open
          recipient={editDetail}
          onClose={() => { setEditRecipient(null); }}
        />
      )}
    </div>
  );
}
